import { ClerkProvider } from '@clerk/react';
import { useAuth, useUser } from '@clerk/react';
import { ClerkAuthContext, defaultClerkAuth } from '@client/hooks/useClerkClientAuth';
import type { ClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { useRef, useEffect, type ReactNode } from 'react';
import {
  registerClerkTokenProvider,
  clearClerkTokenProvider,
} from '@client/api/clerkTokenProvider';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: isAuthLoaded, signOut, getToken } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();

  const isFullyLoaded = isAuthLoaded && isUserLoaded;
  const isClerkSignedIn = isFullyLoaded && !!isSignedIn;

  // Always keep the latest getToken reference — avoids stale closures
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (isClerkSignedIn) {
      registerClerkTokenProvider(() => getTokenRef.current());
    } else {
      clearClerkTokenProvider();
    }
    return () => clearClerkTokenProvider();
  }, [isClerkSignedIn]);

  const value: ClerkClientAuth = {
    isClerkSignedIn,
    isClerkLoading: !isFullyLoaded,
    clerkUser: user,
    clerkEmail: user?.primaryEmailAddress?.emailAddress ?? null,
    clerkUserId: user?.id ?? null,
    clerkSignOut: signOut,
    getClerkToken: getToken,
  };

  return <ClerkAuthContext.Provider value={value}>{children}</ClerkAuthContext.Provider>;
}

export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <ClerkAuthContext.Provider value={defaultClerkAuth}>{children}</ClerkAuthContext.Provider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/client/login"
      signInFallbackRedirectUrl="/client/login"
    >
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  );
}